import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A data visualization tool that displays information as a series of data points connected
          by straight lines, showing the trend or pattern of a variable over time or across
          categories. It is particularly useful for highlighting changes, fluctuations, or
          comparisons, making it ideal for analyzing temporal trends. While effective in visualizing
          data progression, it can sometimes oversimplify complex relationships by focusing
          primarily on the overall trend.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Line charts are ideal for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Tracking Trends Over Time:</strong> Are ideal for visualizing how a specific
            variable changes over time, such as monthly sales, annual temperature changes, or stock
            market performance. They help identify upward or downward trends and patterns across
            time periods.
          </li>
          <li>
            <strong>Comparing Multiple Variables:</strong> Can display multiple data series on the
            same graph, allowing for easy comparison of trends across different categories or
            geographic regions, such as comparing the unemployment rates across different provinces
            or countries over several years.
          </li>
          <li>
            <strong>Visualizing Continuous Data:</strong> Are useful for depicting continuous data
            that can be measured at any point in space or time. This is especially relevant for
            tracking variables like air quality, temperature, or environmental conditions, where
            data points are connected to show smooth transitions or patterns.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Line charts are a valuable data visualization tool, but they have limitations that should
          be considered when deciding whether to use them:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Applicability for Non-Sequential Data:</strong> Line charts are best
            suited for time-based or sequential data. They are not ideal for categorical or
            non-ordered data, as connecting the points may lead to misleading interpretations.
          </li>
          <li>
            <strong>Clarity Issues with Multiple Series:</strong> When displaying multiple data
            series, line charts can become cluttered and difficult to read, especially if the lines
            are too similar or there are too many variables.
          </li>
          <li>
            <strong>Assumptions of Continuity and Small Variations:</strong> Line charts assume
            continuity between data points, which may not be appropriate for discrete data.
            Additionally, small variations in large datasets may be difficult to detect, reducing
            the chart’s effectiveness in showing subtle trends.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is essential in line charts for clarity and comparison. Distinct,
          high-contrast colors differentiate data series, while subtle tones highlight less critical
          trends. Diverging palettes emphasize changes around a central value, and gradual
          transitions show progression or intensity over time.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          While line charts are effective for visualizing trends, they come with challenges that
          should be addressed:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Handling Complex Data Sets:</strong> As data complexity increases, line charts
            can become overwhelming, especially when trying to compare multiple series or variables.
            Ensuring clarity in such cases often requires careful design, such as using different
            colors or styles for each line.
          </li>
          <li>
            <strong>Interpreting Non-Linear Relationships:</strong> Line charts are best suited for
            continuous, linear data. They can be misleading when used for data that follows
            non-linear patterns or for discrete data points, where other types of charts may offer
            better insights.
          </li>
          <li>
            <strong>Accurately Representing Small Data Fluctuations:</strong> In datasets with large
            ranges or minimal changes, line charts can fail to highlight small but significant
            variations, potentially masking important trends or insights.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
